import {
  FilterSelector,
  KeyValue,
} from '@delmonicos/shared-design-system-react/src/components/filter-selector/filter-selector';
import {
  useDebounce,
  usePaginatedFilter,
  usePaginatedFilterControls,
} from '@delmonicos/shared-sdk/libs/core/src/hooks';
import { CpoModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/outputs/cpos/cpo.model';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEmspBoGetCposQuery } from '@app/core/service/emsp-bo-cpo.service';

const LIMIT = 4;

interface EmspBoCpoFilterControllerProps {
  onChange: (itemIds: string[]) => void;
  selectedItemIds: string[];
}

export const EmspBoCpoFilterController = ({
  onChange,
  selectedItemIds,
}: EmspBoCpoFilterControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const [search, setSearch, offset, incrementOffset, resetOffset] =
    usePaginatedFilterControls(LIMIT);
  const [isTyping, setIsTyping] = useState(false);

  const debouncedSearch = useDebounce(search);

  const { data, isFetching } = useEmspBoGetCposQuery({
    limit: LIMIT,
    offset,
    name: debouncedSearch ? `~${debouncedSearch}` : '~',
  });

  const handleMap = useCallback(
    (cpo: CpoModel): KeyValue => ({
      key: cpo.id,
      value: `${cpo.id} - ${cpo.endpoint}`,
    }),
    [],
  );

  const { items, updateItems } = usePaginatedFilter<KeyValue>({
    data,
    offset,
    onMap: handleMap,
  });

  useEffect(() => {
    if (debouncedSearch !== undefined) {
      setIsTyping(false);
    }
  }, [debouncedSearch]);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <FilterSelector
      items={items}
      isLoading={isFetching || isTyping}
      initialSelectedItemIds={selectedItemIds}
      onChange={onChange}
      onSearchChange={(val) => {
        setSearch(val);
        resetOffset();
        updateItems([]);
      }}
      onOffsetChange={incrementOffset}
      labels={{
        title: t('delmonicos-shared-sdk.inputs-from-remote-cpo.cpo-id'),
        search: t('common.filter.search'),
        seeMore: t('common.filter.seeMore'),
      }}
      icon="location"
      hasSeeMore={!!data?.hasNextPage}
    />
  );
};
